import { Trans } from 'react-i18next';
import isEmailValidator from 'validator/lib/isEmail';
import * as Yup from 'yup';

import {
  CONTACT_TYPE,
  CONTACTLESS_VERIFICATION_TYPES,
  FLOW_CODES,
  GIFT_MESSAGE_TYPE,
  MAX_ADDITIONAL_THOUGHTS_LENGTH,
  MAX_EMAIL_LENGTH,
  MAX_FEATURE_NAME_LENGTH,
  MAX_FIRST_NAME_LENGTH,
  MAX_LAST_NAME_LENGTH,
  MAX_SENDER_NAME_LENGTH,
  MAX_SUBJECT_LENGTH,
  MIN_EMAIL_LENGTH,
  MIN_LENGTH,
  VALIDATION_ERRORS
} from '@hero/constants';
import { regex } from '@hero/schemas';
import { getStripedValue } from '@hero/utils';

const getFirstNameRule = () =>
  Yup.string()
    .min(MIN_LENGTH, <Trans key="getFirstNameRule" i18nKey={VALIDATION_ERRORS.minFirstName} />)
    .max(
      MAX_FIRST_NAME_LENGTH,
      <Trans key="getFirstNameRule" i18nKey={VALIDATION_ERRORS.maxFirstName} />
    )
    .required(<Trans key="getFirstNameRule" i18nKey={VALIDATION_ERRORS.noFirstName} />);

const getLastNameRule = () =>
  Yup.string()
    .min(MIN_LENGTH, <Trans key="getLastNameRule" i18nKey={VALIDATION_ERRORS.minLastName} />)
    .max(
      MAX_LAST_NAME_LENGTH,
      <Trans key="getLastNameRule" i18nKey={VALIDATION_ERRORS.maxLastName} />
    )
    .required(<Trans key="getLastNameRule" i18nKey={VALIDATION_ERRORS.noLastName} />);

const getEmailRule = () =>
  Yup.string()
    .email(<Trans key="getEmailRule" i18nKey={VALIDATION_ERRORS.invalidEmail} />)
    .min(MIN_EMAIL_LENGTH, <Trans key="getEmailRule" i18nKey={VALIDATION_ERRORS.minEmail} />)
    .max(MAX_EMAIL_LENGTH, <Trans key="getEmailRule" i18nKey={VALIDATION_ERRORS.maxEmail} />)
    .required(<Trans key="getEmailRule" i18nKey={VALIDATION_ERRORS.noEmail} />)
    .test(
      'is-valid',
      <Trans key="getEmailRule" i18nKey={VALIDATION_ERRORS.invalidEmail} />,
      value => (value ? isEmailValidator(value) : !value)
    );

const getPasswordRule = () =>
  Yup.mixed().test(
    'is-valid-password',
    <Trans key="getPasswordRule" i18nKey={VALIDATION_ERRORS.invalidPassword} />,
    password => {
      let validCount = 0;
      regex.isSpecialChar.test(password) && validCount++;
      regex.isNumber.test(password) && validCount++;
      regex.isLowerCase.test(password) && validCount++;
      regex.isUpperCase.test(password) && validCount++;
      return validCount > 2 && password.length >= 8;
    }
  );

const getInviteInfoSchema = () =>
  Yup.object().shape({
    firstName: getFirstNameRule(),
    lastName: getLastNameRule(),
    email: getEmailRule(),
    password: getPasswordRule(),
    confirmPassword: getPasswordRule().test(
      VALIDATION_ERRORS.invalidConfirmPassword,
      <Trans i18nKey={VALIDATION_ERRORS.invalidConfirmPassword} />,
      function (value) {
        return this.parent.password === value;
      }
    )
  });

//! Step 0
const getNameRule = featureName =>
  Yup.string()
    .required(
      <Trans key="getNameRule" i18nKey={VALIDATION_ERRORS.noFeatureName} values={{ featureName }} />
    )
    .min(
      MIN_LENGTH,
      <Trans
        key="getNameRule"
        i18nKey={VALIDATION_ERRORS.minFeatureName}
        values={{ featureName, minLength: MIN_LENGTH }}
      />
    )
    .max(
      MAX_FEATURE_NAME_LENGTH,
      <Trans
        key="getNameRule"
        i18nKey={VALIDATION_ERRORS.maxFeatureName}
        values={{ featureName, maxLength: MAX_FEATURE_NAME_LENGTH }}
      />
    );

const getTypeRule = featureName =>
  Yup.string().required(
    <Trans key="getTypeRule" i18nKey={VALIDATION_ERRORS.noFeatureType} values={{ featureName }} />
  );

const getNameAndTypeRule = featureName =>
  Yup.object().shape({
    name: getNameRule(featureName),
    type: getTypeRule(featureName)
  });

//! Step 1
const getProductsRule = () =>
  Yup.array()
    .min(1, <Trans key="getProductsRule" i18nKey={VALIDATION_ERRORS.noProductsSelected} />)
    .required(<Trans key="getProductsRule" i18nKey={VALIDATION_ERRORS.noProductsSelected} />);

//! Step 2
const getContactsTypeRule = () =>
  Yup.string()
    .oneOf(['number', 'list'], null)
    .required(
      <Trans
        key="getContactsRule_order=20"
        i18nKey={VALIDATION_ERRORS.noRecipient}
        components={{
          strong: <a href="#section-contact" />
        }}
      />
    );

const getRecipientsTypeRule = () =>
  Yup.string()
    .oneOf(['contacts', 'tags'], null)
    .required(<Trans key="getRecipientsTypeRule" i18nKey={VALIDATION_ERRORS.noIncludeRecipient} />);

//! Step 3
const getContactsRule = ({ featureName = 'recognition', flowCode = FLOW_CODES.standard } = {}) => {
  const noRecipientTypeError = (
    <Trans
      key="getContactsRule_order=20"
      i18nKey={VALIDATION_ERRORS.noRecipient}
      components={{
        strong: <a href="#section-contact" />
      }}
    />
  );

  const noRecipientQuantityTypeError = (
    <Trans
      key="getContactsRule_order=20"
      i18nKey={VALIDATION_ERRORS.noRecipientQuantity}
      values={{ featureName }}
      components={{ strong: <a href="#section-contact" /> }}
    />
  );

  const noTableRecipientsTypeError = (
    <Trans
      key="getContactsRule_order=20"
      i18nKey={VALIDATION_ERRORS.noTableRecipients}
      values={{ featureName }}
      components={{ strong: <a href="#section-contact" /> }}
    />
  );

  const isSimplifiedFlow = flowCode === FLOW_CODES.simplified || flowCode === FLOW_CODES.ai;

  return Yup.object()
    .shape({
      type: getContactsTypeRule(),
      maxCount: Yup.number()
        .typeError(noRecipientTypeError)
        .when('type', {
          is: 'list',
          then: Yup.number()
            .typeError(noRecipientTypeError)
            .min(1, isSimplifiedFlow ? noRecipientTypeError : noTableRecipientsTypeError)
            .required(isSimplifiedFlow ? noRecipientTypeError : noTableRecipientsTypeError)
        })
        .when('type', {
          is: 'number',
          then: Yup.number()
            .typeError(noRecipientTypeError)
            .min(1, noRecipientQuantityTypeError)
            .required(noRecipientQuantityTypeError)
            .typeError(noRecipientQuantityTypeError)
        })
    })
    .required();
};

const getRecipientsRule = ({ featureName = 'automation' } = {}) =>
  Yup.object()
    .shape({
      ids: Yup.array().when('type', {
        is: 'contacts',
        then: Yup.array()
          .min(
            1,
            <Trans
              key="getRecipientsRule"
              i18nKey={VALIDATION_ERRORS.noTableRecipients}
              values={{ featureName }}
            />
          )
          .required(
            <Trans
              key="getRecipientsRule"
              i18nKey={VALIDATION_ERRORS.noTableRecipients}
              values={{ featureName }}
            />
          )
      }),
      tags: Yup.array().when('type', {
        is: 'tags',
        then: Yup.array()
          .min(
            1,
            <Trans
              key="getRecipientsRule"
              i18nKey={VALIDATION_ERRORS.noTableTags}
              values={{ featureName }}
            />
          )
          .required(
            <Trans
              key="getRecipientsRule"
              i18nKey={VALIDATION_ERRORS.noTableTags}
              values={{ featureName }}
            />
          )
      })
    })
    .test(
      'ids-or-tags-required',
      <Trans
        key="getRecipientsRule"
        i18nKey={VALIDATION_ERRORS.noTableRecipientsOrTags}
        values={{ featureName }}
      />,
      value => !!value.ids.length || !!value.tags.length
    )
    .required();

const getRecipientVerificationRule = contacts => {
  return Yup.object().shape({
    type:
      contacts?.type === CONTACT_TYPE.number
        ? Yup.string().required(
            <Trans
              key="getRecipientVerificationRule"
              i18nKey={VALIDATION_ERRORS.noSecuritySetting}
              components={{ strong: <a href="#section-contact" /> }}
            />
          )
        : Yup.string(),
    params: Yup.object().when('type', {
      is: CONTACTLESS_VERIFICATION_TYPES.EMAIL_DOMAIN,
      then: Yup.object()
        .shape({
          emailDomains: Yup.array()
            .min(
              1,
              <Trans
                key="getRecipientVerificationRule"
                i18nKey={VALIDATION_ERRORS.minEmailDomain}
                components={{ strong: <a href="#section-contact" /> }}
              />
            )
            .required(
              <Trans
                key="getRecipientVerificationRule"
                i18nKey={VALIDATION_ERRORS.minEmailDomain}
                components={{ strong: <a href="#section-contact" /> }}
              />
            )
        })
        .required()
    })
  });
};

const getEmailAddressRule = ({ isTypeEmail }) =>
  isTypeEmail
    ? Yup.array()
        .min(1, <Trans key="getEmailAddressRule" i18nKey={VALIDATION_ERRORS.minEmailList} />)
        .required(<Trans key="getEmailAddressRule" i18nKey={VALIDATION_ERRORS.minEmailList} />)
    : Yup.array();

//! Step 4
//! Step 4 - settings
const getSettingsRule = ({ isEndDateRequired }) =>
  Yup.object().when('contacts', (contacts, field) => {
    return field
      .shape({
        expiresAt: isEndDateRequired
          ? Yup.date()
              .min(
                new Date(),
                <Trans key="getSettingsRule" i18nKey={VALIDATION_ERRORS.minEndDate} />
              )
              .required(<Trans key="getSettingsRule" i18nKey={VALIDATION_ERRORS.noEndDate} />)
          : Yup.date(),
        recipientVerification: getRecipientVerificationRule(contacts)
      })
      .required();
  });

const getSettingsAutomationRule = () =>
  Yup.object().when('contacts', (contacts, field) => {
    return field
      .shape({
        allowPremiumDates: Yup.boolean(),
        acceptanceDurationInHours: Yup.number().required(
          <Trans key="getSettingsAutomationRule" i18nKey={VALIDATION_ERRORS.noRedemptionWindow} />
        ),
        occasionScheduleDuration: Yup.object().shape({
          value: Yup.number().required(
            <Trans key="getSettingsAutomationRule" i18nKey={VALIDATION_ERRORS.noSendSchedule} />
          ),
          unit: Yup.string().required(
            <Trans key="getSettingsAutomationRule" i18nKey={VALIDATION_ERRORS.noSendSchedule} />
          )
        })
      })
      .required();
  });

//! Step 4 - messageParams
const getSubjectLineRule = () =>
  Yup.string()
    .test(
      'min-control',
      <Trans key="getSubjectLineRule" i18nKey={VALIDATION_ERRORS.minSubjectLine} />,
      value => {
        if (!value) return true;
        const stripedValue = getStripedValue(value);
        return stripedValue.length >= MIN_LENGTH;
      }
    )
    .test(
      'max-control',
      <Trans key="getSubjectLineRule" i18nKey={VALIDATION_ERRORS.maxSubjectLine} />,
      value => {
        const stripedValue = getStripedValue(value);
        return stripedValue.length <= MAX_SUBJECT_LENGTH;
      }
    )
    .required(<Trans key="getSubjectLineRule" i18nKey={VALIDATION_ERRORS.noSubjectLine} />);

const getSenderNameRule = () =>
  Yup.string()
    .test(
      'min-control',
      <Trans
        key="getSenderNameRule"
        i18nKey={VALIDATION_ERRORS.minSenderName}
        components={{ strong: <a href="#section-recipient-experience" /> }}
      />,
      value => {
        if (!value) return true;
        const stripedValue = getStripedValue(value);
        return stripedValue.length >= MIN_LENGTH;
      }
    )
    .test(
      'max-control',
      <Trans
        key="getSenderNameRule"
        i18nKey={VALIDATION_ERRORS.maxSenderName}
        components={{ strong: <a href="#section-recipient-experience" /> }}
      />,
      value => {
        const stripedValue = getStripedValue(value);
        return stripedValue.length <= MAX_SENDER_NAME_LENGTH;
      }
    )
    .required(
      <Trans
        key="getSenderNameRule"
        i18nKey={VALIDATION_ERRORS.noSenderName}
        components={{ strong: <a href="#section-recipient-experience" /> }}
      />
    );

const getMessageRule = () =>
  Yup.string()
    .test(
      'required-control',
      <Trans
        key="getMessageRule"
        i18nKey={VALIDATION_ERRORS.noGiftCardMessage}
        components={{ strong: <a href="#section-recipient-experience" /> }}
      />,
      value => {
        const stripedValue = getStripedValue(value);
        return stripedValue.length > 0;
      }
    )
    .test(
      'min-control',
      <Trans
        key="getMessageRule"
        i18nKey={VALIDATION_ERRORS.minGiftCardMessage}
        components={{ strong: <a href="#section-recipient-experience" /> }}
      />,
      value => {
        const stripedValue = getStripedValue(value);
        return stripedValue.length >= MIN_LENGTH;
      }
    );

const getAboutMessageRule = () =>
  Yup.string()
    .optional()
    .test(
      'min-control',
      <Trans key="getAboutMessageRule" i18nKey={VALIDATION_ERRORS.minAboutMessage} />,
      value => {
        const stripedValue = getStripedValue(value);
        if (!stripedValue) return true;
        return stripedValue.length >= MIN_LENGTH;
      }
    )
    .test(
      'max-control',
      <Trans key="getAboutMessageRule" i18nKey={VALIDATION_ERRORS.maxAboutMessage} />,
      value => {
        const stripedValue = getStripedValue(value);
        return stripedValue.length <= MAX_ADDITIONAL_THOUGHTS_LENGTH;
      }
    );

const getMessageParamsRule = () =>
  Yup.object().shape({
    senderName: getSenderNameRule(),
    aboutMessage: getAboutMessageRule(),
    message: getMessageRule()
  });

const getMessageAndCardParamsRule = giftMessageType =>
  giftMessageType === GIFT_MESSAGE_TYPE.inputMessage
    ? getMessageParamsRule()
    : giftMessageType === GIFT_MESSAGE_TYPE.cardMessage
      ? Yup.object().shape({
          senderName: getSenderNameRule(),
          aboutMessage: getAboutMessageRule(),
          digitalGreetingCard: Yup.boolean().test(
            false,
            <Trans key="noDigitalCard" i18nKey={VALIDATION_ERRORS.noDigitalCard} />,
            value => value
          )
        })
      : Yup.object().shape({
          digitalGreetingCard: Yup.boolean()
            .test(
              false,
              <Trans
                key="noGiftCardOrDigitalCardMessage"
                i18nKey={VALIDATION_ERRORS.noGiftCardOrDigitalCardMessage}
              />,
              value => value
            )
            .required()
        });

//! Step 4 - branding
const getGiftWrapperUrlRule = () =>
  Yup.string()
    .min(MIN_LENGTH)
    .required(
      <Trans key="getGiftWrapperUrlRule" i18nKey={VALIDATION_ERRORS.noGiftWrapperSelected} />
    )
    .typeError(
      <Trans key="getGiftWrapperUrlRule" i18nKey={VALIDATION_ERRORS.noGiftWrapperSelected} />
    );

const getBrandingRule = () =>
  Yup.object().shape({
    giftWrapperUrl: getGiftWrapperUrlRule()
  });

//! Empty sharing method
// sharingMethod: type: []
//! You'll send it sharingMethod
// sharingMethod: type: ['none']
//! We'll send it sharingMethod
// sharingMethod: type: ['email']
const getSharingMethodsRule = () =>
  Yup.object().shape({
    type: Yup.array().min(
      1,
      <Trans
        key="getSharingMethodsRule_order=30"
        i18nKey={VALIDATION_ERRORS.noSendingMethodSelected}
        components={{ strong: <a href="#section-how-to-send" /> }}
      />
    )
  });

const getExperienceSettingsRule = ({ isInputMessageRequired, flowCode }) => {
  const isSimplifiedFlow = flowCode === FLOW_CODES.simplified || flowCode === FLOW_CODES.ai;

  return Yup.object().shape({
    branding: getBrandingRule(),
    messageParams: getMessageAndCardParamsRule(
      isInputMessageRequired ? GIFT_MESSAGE_TYPE.inputMessage : GIFT_MESSAGE_TYPE.cardMessage
    ),
    ...(isSimplifiedFlow ? { sharingMethod: getSharingMethodsRule() } : {})
  });
};

//! Step 5
const getPaymentMethodRule = isPaymentMethodRequired =>
  isPaymentMethodRequired
    ? Yup.object()
        .shape({
          code: Yup.string().required(
            <Trans key="getPaymentMethodRule" i18nKey={VALIDATION_ERRORS.noPaymentMethod} />
          ), // ex: "142" etc
          type: Yup.string().required(
            <Trans key="getPaymentMethodRule" i18nKey={VALIDATION_ERRORS.noPaymentMethod} />
          ) // ex: "credit-card" or "house-account"
        })
        .typeError(<Trans key="getPaymentMethodRule" i18nKey={VALIDATION_ERRORS.noPaymentMethod} />)
        .required(<Trans key="getPaymentMethodRule" i18nKey={VALIDATION_ERRORS.noPaymentMethod} />)
    : Yup.object().shape({
        code: Yup.string(),
        type: Yup.string()
      });

//! Others
const getMetaRule = ({ isCostCenterRequired, isPoNumberRequired }) =>
  Yup.object().shape({
    poNumber: isPoNumberRequired
      ? Yup.string().required(
          <Trans key="getMetaPoNumberRule" i18nKey={VALIDATION_ERRORS.noPoNumber} />
        )
      : Yup.string(),
    costCenter: isCostCenterRequired
      ? Yup.string().required(
          <Trans key="getMetaCostCenterRule" i18nKey={VALIDATION_ERRORS.noCostCenter} />
        )
      : Yup.string()
  });

export {
  getBrandingRule,
  getContactsRule,
  getContactsTypeRule,
  getEmailAddressRule,
  getEmailRule,
  getExperienceSettingsRule,
  getFirstNameRule,
  getInviteInfoSchema,
  getLastNameRule,
  getMessageAndCardParamsRule,
  getMessageParamsRule,
  getMessageRule,
  getMetaRule,
  getNameAndTypeRule,
  getNameRule,
  getPasswordRule,
  getPaymentMethodRule,
  getProductsRule,
  getRecipientVerificationRule,
  getRecipientsRule,
  getRecipientsTypeRule,
  getSenderNameRule,
  getSettingsAutomationRule,
  getSettingsRule,
  getSubjectLineRule,
  getTypeRule
};
